import { useEffect, useRef, useState } from 'react';
import { decodeOffer, linkVerificationCode } from '@vav/client-core';
import type { VavClient, DeviceSummary } from '@vav/client-core';
import { Avatar } from './Avatar.tsx';
import { formatFullDate } from '../format.ts';
import { useT } from '../i18n.tsx';
import { useInstallPrompt } from '../useInstallPrompt.ts';
import { createProfile, listProfiles, removeProfile } from '../profiles.ts';
import { AvatarCropDialog } from './AvatarCropDialog.tsx';
import type { ProfileSummary } from '../backgroundProfiles.ts';

/** The content hash of the running bundle, e.g. `BhG3u4G6`. */
function buildId(): string {
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[src]')) {
    const match = /index-([A-Za-z0-9_-]+)\.js/.exec(script.src);
    if (match) return match[1];
  }
  return 'dev';
}

/**
 * A diagnostic, not a feature: which audio outputs this browser will admit
 * to. Whether the speaker button can exist on a given phone is decided by
 * exactly this list, and a screenshot of this line answers it remotely.
 * Latin-only on purpose — it is for debugging across a screenshot.
 */
function AudioOutputsProbe() {
  const [line, setLine] = useState('');
  useEffect(() => {
    void (async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const outs = devices.filter((d) => d.kind === 'audiooutput');
        setLine(
          `audio out: ${outs.length}` +
            (outs.length ? ` (${outs.map((d) => d.label || d.deviceId.slice(0, 8) || '?').join(', ')})` : ''),
        );
      } catch {
        setLine('audio out: unavailable');
      }
    })();
  }, []);
  return (
    <p className="profile__build" dir="ltr">
      {line}
    </p>
  );
}

/**
 * Who you are on this server, and how to get the app onto the home screen.
 *
 * Deliberately read-only about identity. The username is the account and the
 * server has no route to change either it or the display name, so offering an
 * edit box here would be a promise the protocol cannot keep. Phone linking
 * lives in its own dialog because it is discovery, not identity -- see the
 * nullable `phone_hash` column.
 *
 * There is no sign-out button, and that is not an omission. `logout()` clears
 * the store, the store holds the only copy of the identity key, and there is no
 * account recovery: the button would read as "sign out" and mean "destroy this
 * account". If it is ever added it needs a typed confirmation, not a tap.
 */
export function ProfileDialog({
  client,
  profileId,
  profiles,
  onSwitchProfile,
  onClose,
}: {
  client: VavClient;
  profileId: string;
  profiles: ProfileSummary[];
  onSwitchProfile: (id: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const account = client.account;
  const [devices, setDevices] = useState<DeviceSummary[]>();
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<string>();
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(account?.displayName ?? '');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [cropFile, setCropFile] = useState<File>();
  const avatarInput = useRef<HTMLInputElement>(null);

  async function applyCropped(bytes: Uint8Array, meta: { mime: string; name: string }) {
    setCropFile(undefined);
    setAvatarBusy(true);
    try {
      await client.setAvatar(bytes, meta);
    } catch {
      // The next attempt is one tap away; a toast here would outlive its use.
    } finally {
      setAvatarBusy(false);
    }
  }

  async function saveName() {
    const next = draftName.trim();
    if (!next || next === account?.displayName) {
      setEditingName(false);
      return;
    }
    setNameBusy(true);
    setNameError(false);
    try {
      await client.setDisplayName(next);
      setEditingName(false);
    } catch {
      setNameError(true);
    } finally {
      setNameBusy(false);
    }
  }

  async function reallyDelete() {
    setDeleteBusy(true);
    setDeleteError(false);
    try {
      await client.deleteAccount();
      // The store is gone, and with it every key. Nothing in memory is worth
      // keeping, and a reload is the only honest way back to a first run.
      window.location.reload();
    } catch {
      setDeleteError(true);
      setDeleteBusy(false);
    }
  }
  const { state: installState, install } = useInstallPrompt();
  const [updateState, setUpdateState] = useState<'idle' | 'busy' | 'current'>('idle');

  /**
   * Look for a new version right now, and reload into it if there is one.
   *
   * The first version of this button only asked the service worker to update —
   * which checks whether *sw.js* changed, and sw.js rarely does. A deploy that
   * changed only app code found "no update", said "you're current", and left
   * the app stale: the version that matters is named by index.html, so that is
   * what must be compared. Fetch the server's index.html fresh, read the
   * bundle hash out of it, and reload when it differs — navigations are
   * network-first, so the reload lands on the new version. The worker is still
   * asked to update alongside, for the rare deploy where sw.js did change.
   */
  async function checkForUpdate() {
    setUpdateState('busy');
    try {
      const registration = await navigator.serviceWorker?.getRegistration();
      void registration?.update();

      const response = await fetch('/', { cache: 'no-store' });
      const html = await response.text();
      const served = /index-([A-Za-z0-9_-]+)\.js/.exec(html)?.[1];
      if (served && served !== buildId()) {
        window.location.reload();
        return;
      }
      setUpdateState('current');
    } catch {
      setUpdateState('idle');
    }
  }

  function addProfile() {
    // Created and switched to in one step: a profile you are not taken to is an
    // empty row that does nothing until you find the switcher again.
    const profile = createProfile(newLabel);
    onSwitchProfile(profile.id);
    onClose();
  }

  async function remove(id: string) {
    await removeProfile(id);
    setConfirmRemove(undefined);
    // Switching to whatever survived is the caller's job; ask for the first.
    const remaining = listProfiles();
    if (id === profileId && remaining[0]) onSwitchProfile(remaining[0].id);
  }

  /** Remote disconnect and local logout, both only meaningful with >1 device. */
  const [removingId, setRemovingId] = useState<number>();
  const [logoutStep, setLogoutStep] = useState<'closed' | 'confirm' | 'busy'>('closed');

  async function disconnectDevice(deviceId: number) {
    setRemovingId(deviceId);
    try {
      await client.removeDevice(deviceId);
      setDevices(await client.listMyDevices().catch(() => (devices ?? []).filter((d) => d.deviceId !== deviceId)));
    } catch {
      // Offline; the list stays as it was and the button can be pressed again.
    }
    setRemovingId(undefined);
  }

  async function logoutHere() {
    setLogoutStep('busy');
    // Deregister first, while the token still works; the local wipe is the
    // part that must not fail, and it cannot.
    if (account) await client.removeDevice(account.deviceId).catch(() => undefined);
    await client.logout();
    window.location.reload();
  }

  /** Approving a new device: paste, compare digits, approve. */
  const [linkState, setLinkState] = useState<'closed' | 'input' | 'busy' | 'done' | 'failed'>('closed');
  const [linkCode, setLinkCode] = useState('');
  const linkDigits = (() => {
    try {
      return linkVerificationCode(decodeOffer(linkCode.trim()).ephemeralPub);
    } catch {
      return undefined;
    }
  })();

  async function approveLink() {
    setLinkState('busy');
    try {
      await client.approveLinking(linkCode.trim());
      setLinkState('done');
      // The list below now has one more row; refresh it.
      setDevices(await client.listMyDevices().catch(() => devices));
    } catch {
      setLinkState('failed');
    }
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const list = await client.listMyDevices();
        if (!cancelled) setDevices(list);
      } catch {
        // Offline, most likely. The rest of the dialog is local and still works.
        if (!cancelled) setDevices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onClick={onClose}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('profile.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dialog__header">
          <h2>{t('profile.title')}</h2>
          <button className="icon" onClick={onClose} aria-label={t('bubble.closeImage')}>
            ✕
          </button>
        </header>

        <div className="profile__identity">
          {/* Tapping your own face is how every messenger changes it. */}
          <button
            className="profile__avatarButton"
            onClick={() => avatarInput.current?.click()}
            disabled={avatarBusy}
            aria-label={t('profile.avatar.change')}
            title={t('profile.avatar.change')}
          >
            <Avatar name={account?.displayName ?? '?'} size={56} avatar={account?.avatar} client={client} />
            <span className="profile__avatarBadge" aria-hidden="true">📷</span>
          </button>
          <input
            ref={avatarInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setCropFile(file);
              e.target.value = '';
            }}
          />
          {cropFile && (
            <AvatarCropDialog
              file={cropFile}
              onCancel={() => setCropFile(undefined)}
              onDone={(bytes, meta) => void applyCropped(bytes, meta)}
            />
          )}
          <div className="profile__identityText">
            {editingName ? (
              <div className="profile__nameEdit">
                <input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void saveName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  maxLength={64}
                  autoFocus
                  dir="auto"
                />
                <button className="primary" disabled={nameBusy} onClick={() => void saveName()}>
                  {t('profile.name.save')}
                </button>
                <button onClick={() => setEditingName(false)}>{t('profile.name.cancel')}</button>
              </div>
            ) : (
              <>
                <strong>{account?.displayName}</strong>
                <button
                  className="profile__nameButton"
                  onClick={() => {
                    setDraftName(account?.displayName ?? '');
                    setEditingName(true);
                  }}
                >
                  {t('profile.name.edit')}
                </button>
              </>
            )}
            {nameError && <span className="error">{t('profile.name.failed')}</span>}
            {account?.avatar && (
              <button className="profile__nameButton" onClick={() => void client.clearAvatar()}>
                {t('profile.avatar.remove')}
              </button>
            )}
            {/* The handle reads left-to-right even in an RTL page: it is an
                identifier, not prose. It is also the account, and cannot move. */}
            <div className="muted" dir="ltr">
              @{account?.username}
            </div>
          </div>
        </div>

        <dl className="profile__facts">
          <dt>{t('profile.username')}</dt>
          <dd dir="ltr">@{account?.username}</dd>
          <dt>{t('profile.thisDevice')}</dt>
          <dd dir="ltr">#{account?.deviceId}</dd>
        </dl>

        <section className="profile__section">
          <h3>{t('profile.profiles')}</h3>
          <p className="hint">{t('profile.profiles.explain')}</p>

          <ul className="profile__list">
            {profiles.map((profile) => {
              const isCurrent = profile.profileId === profileId;
              // Only the *other* profiles are tracked in the background, so the
              // current one has no summary to read — its account is right here.
              const displayName = isCurrent ? account?.displayName : profile.displayName;
              const username = isCurrent ? account?.username : profile.username;
              const name = profile.label || displayName || t('profile.profiles.unnamed');
              return (
                <li key={profile.profileId}>
                  <button
                    className={`profile__switch ${isCurrent ? 'is-current' : ''}`}
                    onClick={() => {
                      if (!isCurrent) onSwitchProfile(profile.profileId);
                    }}
                    aria-current={isCurrent}
                  >
                    <Avatar name={displayName ?? name} size={30} />
                    <span className="profile__switchText">
                      <span>{name}</span>
                      {username && (
                        <small className="muted" dir="ltr">
                          @{username}
                        </small>
                      )}
                    </span>
                    {/* The whole reason the other profiles stay connected. */}
                    {profile.unread > 0 && !isCurrent && (
                      <span className="profile__unread">{profile.unread}</span>
                    )}
                    {isCurrent && <span className="profile__thisDevice">{t('profile.deviceCurrent')}</span>}
                  </button>

                  {profiles.length > 1 &&
                    (confirmRemove === profile.profileId ? (
                      <button className="danger" onClick={() => void remove(profile.profileId)}>
                        {t('profile.profiles.removeConfirm')}
                      </button>
                    ) : (
                      <button
                        className="icon"
                        onClick={() => setConfirmRemove(profile.profileId)}
                        aria-label={t('profile.profiles.remove')}
                        title={t('profile.profiles.remove')}
                      >
                        🗑️
                      </button>
                    ))}
                </li>
              );
            })}
          </ul>

          {adding ? (
            <div className="profile__add">
              <input
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder={t('profile.profiles.namePlaceholder')}
                dir="auto"
                autoFocus
              />
              <button className="primary" onClick={addProfile}>
                {t('profile.profiles.create')}
              </button>
            </div>
          ) : (
            <button onClick={() => setAdding(true)}>➕ {t('profile.profiles.add')}</button>
          )}

          {/* Said before they commit, not after: a new profile is a new account
              with a new username, not another view of this one. */}
          <p className="hint">{t('profile.profiles.warning')}</p>
        </section>

        <section className="profile__section">
          <h3>{t('profile.devices')}</h3>
          {devices === undefined ? (
            <p className="muted">{t('profile.devicesLoading')}</p>
          ) : devices.length === 0 ? (
            <p className="muted">{t('profile.devicesNone')}</p>
          ) : (
            <ul className="profile__devices">
              {devices.map((device) => (
                <li key={device.deviceId}>
                  <span dir="ltr">#{device.deviceId}</span>
                  <span className="muted">{formatFullDate(device.createdAt)}</span>
                  {device.deviceId === account?.deviceId ? (
                    <span className="profile__thisDevice">{t('profile.deviceCurrent')}</span>
                  ) : (
                    /* Cutting a device off is reversible — link it again — so
                       one press is enough ceremony. */
                    <button
                      className="profile__nameButton"
                      disabled={removingId === device.deviceId}
                      onClick={() => void disconnectDevice(device.deviceId)}
                    >
                      {t('profile.device.remove')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/*
            Log out exists only while the account also lives somewhere else.
            On the last device this button would be the delete-account button
            wearing a friendlier name, and it stays hidden rather than lying.
          */}
          {(devices?.length ?? 0) > 1 &&
            (logoutStep === 'closed' ? (
              <button onClick={() => setLogoutStep('confirm')}>{t('profile.logout')}</button>
            ) : (
              <div className="profile__deleteConfirm">
                <p className="hint">{t('profile.logout.explain')}</p>
                <span className="profile__deleteActions">
                  <button
                    className="danger"
                    disabled={logoutStep === 'busy'}
                    onClick={() => void logoutHere()}
                  >
                    {t('profile.logout.confirm')}
                  </button>
                  <button onClick={() => setLogoutStep('closed')}>{t('profile.name.cancel')}</button>
                </span>
              </div>
            ))}

          {/*
            Approving a new device. The pasted code holds only public parts;
            what crosses to the new device is sealed to the ephemeral key
            inside it. The six digits shown here are derived from that key —
            the person compares them with the new device's screen, and the
            match is what defeats a swapped code.
          */}
          {linkState === 'closed' ? (
            <button onClick={() => setLinkState('input')}>🔗 {t('profile.link.start')}</button>
          ) : (
            <div className="profile__linkFlow">
              <p className="hint">{t('profile.link.hint')}</p>
              <input
                value={linkCode}
                onChange={(e) => setLinkCode(e.target.value)}
                placeholder="vav://link?..."
                autoComplete="off"
                dir="ltr"
              />
              {linkDigits && (
                <>
                  <p className="link__verify" dir="ltr">{linkDigits}</p>
                  <p className="hint">{t('profile.link.verifyHint')}</p>
                </>
              )}
              {linkState === 'failed' && <span className="error">{t('profile.link.failed')}</span>}
              {linkState === 'done' && <span className="muted">✓ {t('profile.link.done')}</span>}
              <span className="profile__deleteActions">
                <button
                  className="primary"
                  disabled={!linkDigits || linkState === 'busy' || linkState === 'done'}
                  onClick={() => void approveLink()}
                >
                  {t('profile.link.approve')}
                </button>
                <button onClick={() => { setLinkState('closed'); setLinkCode(''); }}>
                  {t('profile.name.cancel')}
                </button>
              </span>
            </div>
          )}
        </section>

        <section className="profile__section">
          <h3>{t('profile.install')}</h3>
          {installState === 'installed' ? (
            <p className="muted">{t('profile.install.already')}</p>
          ) : installState === 'available' ? (
            <button className="primary" onClick={() => void install()}>
              {t('profile.install.action')}
            </button>
          ) : installState === 'manual-ios' ? (
            <p className="muted">{t('profile.install.ios')}</p>
          ) : (
            // The honest message. This is the state the user hit: the app is
            // still installed as far as the browser is concerned, so it will
            // not offer to install it again.
            <p className="muted">{t('profile.install.unavailable')}</p>
          )}
        </section>

        {/* The one thing about this account nobody can undo, said once, here,
            where someone reading about their account will actually see it. */}
        <p className="hint">{t('profile.noRecovery')}</p>

        {/*
          The only way out of the product, and deliberately the only one: there
          is no "log out". `logout()` clears the store, the store holds the only
          copy of the identity key, and nothing can sign back in -- so a button
          saying "log out" would promise a door that does not exist.

          Confirmed by typing the username rather than by a tap. This is the one
          action in the app that nothing can undo.
        */}
        <section className="profile__section profile__danger">
          <h3>{t('profile.delete')}</h3>
          {!deleting ? (
            <button className="danger" onClick={() => setDeleting(true)}>
              {t('profile.delete')}
            </button>
          ) : (
            <div className="profile__deleteConfirm">
              <p className="hint">{t('profile.delete.explain')}</p>
              <p className="hint">{t('profile.delete.others')}</p>
              <label htmlFor="confirm-delete">{t('profile.delete.confirmPrompt')}</label>
              <input
                id="confirm-delete"
                value={deleteTyped}
                onChange={(e) => setDeleteTyped(e.target.value)}
                placeholder={account?.username}
                autoComplete="off"
                dir="ltr"
              />
              {deleteError && <span className="error">{t('profile.delete.failed')}</span>}
              <span className="profile__deleteActions">
                <button
                  className="danger"
                  disabled={deleteBusy || deleteTyped.trim() !== account?.username}
                  onClick={() => void reallyDelete()}
                >
                  {t('profile.delete.confirm')}
                </button>
                <button onClick={() => { setDeleting(false); setDeleteTyped(''); }}>
                  {t('profile.name.cancel')}
                </button>
              </span>
            </div>
          )}
        </section>

        {/*
          Which build is actually running.

          Taken from the loaded script's own filename rather than injected at
          build time: the hash is already there, already changes with every
          build, and cannot drift from the code it names. It exists because an
          installed PWA serves whatever its service worker cached, so "is this
          fixed?" and "is this the fixed version?" are different questions and
          there was no way to tell them apart.
        */}
        <AudioOutputsProbe />
        <p className="profile__build" dir="ltr">
          {t('profile.build', { build: buildId() })}
          {' · '}
          {updateState === 'current' ? (
            <span>{t('profile.checkUpdate.current')}</span>
          ) : (
            <button
              className="profile__nameButton"
              disabled={updateState === 'busy'}
              onClick={() => void checkForUpdate()}
            >
              {updateState === 'busy' ? t('profile.checkUpdate.busy') : t('profile.checkUpdate')}
            </button>
          )}
        </p>
      </div>
    </div>
  );
}
